export const LOCALES = ['en', 'de'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * Picks the UI locale from the browser's language preferences
 * (`packages/translations` ships en + de). Falls back to English.
 */
export function detectLocale(
  languages: readonly string[] = typeof navigator === 'undefined'
    ? []
    : (navigator.languages ?? [navigator.language]),
): Locale {
  for (const language of languages) {
    const base = language.split('-')[0]?.toLowerCase();
    if (base && isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

/**
 * Browser hints sent with sign-up so the account starts with the right locale
 * and timezone (docs/auth/40-features/signup.md AC10). Anything unusable is
 * dropped by the contract rather than failing the request.
 */
export function browserPreferences(): { locale?: string; zoneinfo?: string } {
  if (typeof navigator === 'undefined') return {};
  const locale = navigator.languages?.[0] ?? navigator.language;
  let zoneinfo: string | undefined;
  try {
    zoneinfo = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    zoneinfo = undefined;
  }
  return { ...(locale && { locale }), ...(zoneinfo && { zoneinfo }) };
}

/**
 * How each locale presents in the selector. The matching flag artwork lives
 * in `components/flag-marks.tsx`.
 */
export const LOCALE_META: Record<Locale, { label: string }> = {
  // Each label is in ITS OWN language on purpose: a German speaker lost on an
  // English screen must be able to find "Deutsch" without reading English.
  en: { label: 'English' },
  de: { label: 'Deutsch' },
};

const STORED_LOCALE_KEY = 'buds-auth:locale';

/**
 * The locale the user explicitly picked, or null if they never touched the
 * selector. Distinct from `detectLocale()` on purpose: only an explicit choice
 * is persisted to the account later — a detected browser language must never
 * overwrite a stored preference just because someone signed in from a hotel
 * machine. Storage access is guarded: Safari's private mode throws.
 */
export function storedLocale(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORED_LOCALE_KEY);
    return value !== null && isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORED_LOCALE_KEY, locale);
  } catch {
    // The selection still applies for this page; it just will not survive.
  }
}
